import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { notify } from 'src/clients/ApiService';
import { ERROR_CONSTS } from 'src/constants';
import {
  ContractBuilderContext,
  ContractFile,
} from 'src/context/contractBuilderContext';
import { ContractField } from 'src/entities/Contract';
import { useContractBuilderAndSignerForm } from 'src/hooks/useContractBuilderAndSigner';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { ContractTemplateForm } from 'src/legacy/components/Contracts/ContractBuilderAndSignerForm';
import { FormPage } from 'src/legacy/components/Page/FormPage';
import {
  AddContractTemplateInput,
  ApiTags,
  appAPI,
  useAddContractTemplateMutation,
  useShareContractTemplateMutation,
} from 'src/services/api';
import {
  loadContractPages,
  ResetESignToInitialValues,
} from 'src/store/signaturePage/actions';
import { alertSnackbar, setFormPageChanged } from 'src/store/ui/actions';
import {
  filterSignatureComponentsByCreationMethod,
  getContractInputValueUsingComponentType,
  hasValidClientContractComponents,
} from 'src/utils/ContractUtils';

import { ContractBuilderModalHeader } from './ContractBuilderModalHeader';

export interface CreatedContractData {
  templateId: string;
  templateName: string;
}

interface ContractBuilderModalContentProps {
  onClose: (createdContract?: CreatedContractData) => void;
  initialTitle?: string;
}

export const ContractBuilderModalContent = ({
  onClose,
  initialTitle,
}: ContractBuilderModalContentProps) => {
  const dispatch = useAppDispatch();

  const { pageComponents } = useAppSelector((state) => ({
    pageComponents: state.signaturePage.pageComponents,
  }));

  const [addContractTemplate] = useAddContractTemplateMutation();
  const [createContract] = useShareContractTemplateMutation();

  const [isOneOffContract, setIsOneOffContract] = useState(false);
  const [pageScalerFactor, setPageScalerFactor] = useState(1);
  const [contractPdfInfo, setContractPdfInfo] = useState<ContractFile>({
    fileKey: '',
    pageFileKeys: [],
    identityId: '',
  });
  const [placeholderElement, setPlaceholderElement] =
    useState<HTMLButtonElement | null>(null);
  const [isFontLoaded, setIsFontLoaded] = useState(false);

  // Load Cedarville Cursive font for signature components
  useEffect(() => {
    if (!isFontLoaded) {
      const font = new FontFace(
        'Cedarville Cursive',
        'url(https://fonts.gstatic.com/s/cedarvillecursive/v17/yYL00g_a2veiudhUmxjo5VKkoqA-B_nuIrpw4cNOTw.woff2)',
      );
      document.fonts.add(font);
      font
        .load()
        .then(() => {
          setIsFontLoaded(true);
        })
        .catch(() => {
          // Font load failed (network issue) — signature components will use fallback font
        });
    }
  }, [isFontLoaded]);

  // Load contract page images when PDF info changes
  useEffect(() => {
    if (contractPdfInfo?.pageFileKeys?.length) {
      dispatch(loadContractPages(contractPdfInfo, []));
    }
  }, [contractPdfInfo, dispatch]);

  // Reset signature page redux state on unmount
  useEffect(() => {
    return () => {
      dispatch(ResetESignToInitialValues());
    };
  }, [dispatch]);

  // Use refs for values that change frequently but are only needed in handleSave,
  // so we don't recreate the callback and trigger cascading re-renders.
  const pageComponentsRef = useRef(pageComponents);
  pageComponentsRef.current = pageComponents;
  const isOneOffContractRef = useRef(isOneOffContract);
  isOneOffContractRef.current = isOneOffContract;
  const pageScalerFactorRef = useRef(pageScalerFactor);
  pageScalerFactorRef.current = pageScalerFactor;
  const contractPdfInfoRef = useRef(contractPdfInfo);
  contractPdfInfoRef.current = contractPdfInfo;

  const handleSave = useCallback(
    async (values: ContractTemplateForm) => {
      const filteredPageComponents = filterSignatureComponentsByCreationMethod(
        pageComponentsRef.current,
        !isOneOffContractRef.current,
      );

      const areAllComponentsAssignedToPage = filteredPageComponents.every(
        (comp) => Boolean(comp.page),
      );

      if (!areAllComponentsAssignedToPage) {
        dispatch(
          alertSnackbar({
            errorMessage: ERROR_CONSTS.SignatureComponentMissingPage,
          }),
        );
        return;
      }

      if (!hasValidClientContractComponents(filteredPageComponents)) {
        dispatch(
          alertSnackbar({
            errorMessage: ERROR_CONSTS.AddComponentForClient,
          }),
        );
        return;
      }

      const currentPageScalerFactor = pageScalerFactorRef.current;
      const currentContractPdfInfo = contractPdfInfoRef.current;

      const contractFields: Array<ContractField> = filteredPageComponents.map(
        (p) => ({
          id: p.key,
          label: p.label,
          page: p.page ?? 0,
          type: p.fieldType!,
          inputType: p.inputType,
          isOptional: Boolean(p.isOptional),
          value: getContractInputValueUsingComponentType(p),
          autoFillField: p.autoFillField,
          bounds: {
            x: p.xPosition * currentPageScalerFactor,
            y: p.yPosition * currentPageScalerFactor,
            height: p.height * currentPageScalerFactor,
            width: p.width * currentPageScalerFactor,
            pageWidth: p.pageWidth ?? 0,
            pageHeight: p.pageHeight ?? 0,
          },
        }),
      );

      const requestBody: AddContractTemplateInput = {
        name: values.title,
        fileKey: currentContractPdfInfo.fileKey,
        identityId: currentContractPdfInfo.identityId,
        fields: contractFields,
      };

      if (isOneOffContractRef.current) {
        const { clientId, companyId } = values;
        if (values.recipientId || (clientId && companyId)) {
          const response = await createContract({
            ...requestBody,
            recipientId: values.recipientId || '',
            clientId: clientId || '',
            companyId: companyId || '',
            creationMode: 'oneOff',
          })
            .unwrap()
            .catch(
              (e: { data?: { error?: unknown } }) => e?.data ?? { error: true },
            );

          if (!response || 'error' in response) {
            notify({
              status: 'error',
              errorMessage: 'Something went wrong while sending the contract',
              dispatch,
            });
            return;
          }
          notify({
            status: 'success',
            successMessage: 'Contract shared successfully.',
            dispatch,
          });
          dispatch(appAPI.util.invalidateTags([ApiTags.contracts]));
          dispatch(setFormPageChanged(false));
          dispatch(ResetESignToInitialValues());
          onClose();
        }
      } else {
        const result = await addContractTemplate(requestBody);

        if ('data' in result) {
          onClose({
            templateId: result.data.id,
            templateName: result.data.name,
          });
        } else {
          notify({
            status: 'error',
            errorMessage: 'Something went wrong while creating the template',
            dispatch,
          });
        }
      }
    },
    [dispatch, onClose, addContractTemplate, createContract],
  );

  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const contextValue: ContractBuilderContext = useMemo(
    () => ({
      placeholderElement,
      contractPdfInfo,
      isOneOffContract,
      pageScalerFactor,
      setPlaceholderElement,
      setContractPdfInfo,
      setIsOneOffContract,
      setPageScalerFactor,
    }),
    [placeholderElement, contractPdfInfo, isOneOffContract, pageScalerFactor],
  );

  const getSubmitActionLabel = useMemo(() => {
    return isOneOffContract ? 'Send' : 'Create template';
  }, [isOneOffContract]);

  const initialFormValue = useMemo(
    () => ({
      title: initialTitle ?? '',
      fileKey: '',
      pageComponents: [],
    }),
    [initialTitle],
  );

  return (
    <ContractBuilderContext.Provider value={contextValue}>
      <FormPage
        useFormHook={useContractBuilderAndSignerForm}
        HeaderComponent={ContractBuilderModalHeader}
        headerVariant="bottomBorder"
        cancelLabel="Cancel"
        actionLabel={getSubmitActionLabel}
        handleCancel={onCancel}
        shouldShowPageAction
        handleSave={handleSave}
        initialFormValue={initialFormValue}
        title="eSignature request"
        disableSubmit={false}
      />
    </ContractBuilderContext.Provider>
  );
};
